import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  dreamTeamAsPickLight,
  getEventDreamTeam,
  getEventDreamTeamFormation,
  getOverallDreamTeam,
  getOverallDreamTeamFormation,
} from "core-integration/src/store/dream-teams/reducers";
import {
  fetchEventDreamTeam,
  fetchOverallDreamTeam,
} from "core-integration/src/store/dream-teams/thunks";
import {
  getElementsById,
  getElementsEventDataById,
} from "core-integration/src/store/elements/reducers";
import {
  fetchEventLive,
  showElementSummary,
} from "core-integration/src/store/elements/thunks";
import { IPickLight } from "core-integration/src/store/entries/types";
import {
  getCurrentEvent,
  getEventsById,
} from "core-integration/src/store/events/reducers";
import { getFixturesForEventById } from "core-integration/src/store/fixtures/reducers";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { useEffect, useState } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { Box } from "rebass/styled-components";
import styled from "styled-components";
import BoldLink from "../BoldLink";
import BoldLinkButton from "../BoldLinkButton";
import Button from "../Button";
import ElementShirt from "../ElementShirt";
import Fixtures from "../Fixtures";
import { ControlArrowLeft, ControlArrowRight } from "../icons/Arrows";
import { Main, Secondary, Wrapper } from "../Layout";
import Media from "../Media";
import {
  EventPager,
  PagerButton,
  PagerButtonNext,
  PagerHeading,
} from "../Pager";
import { NotFound } from "../Routes";
import SubHeading from "../SubHeading";
import TabPanel from "../tabs/TabPanel";
import Tabs from "../tabs/Tabs";
import Title from "../Title";
import DreamTeamFormation from "./DreamTeamFormation";
import DreamTeamTable from "./DreamTeamTable";
import ElementExplainDialog from "./ElementExplainDialog";
import valueForPlayerElement from "./valueForPlayedElement";

const DTScoreboardWrapper = styled.div`
  margin: ${({ theme }) => theme.space[4]} 0;
  padding: ${({ theme }) => theme.space[2]};
  border: ${({ theme }) => theme.borders[2]};
  border-radius: ${({ theme }) => theme.radii[2]};
`;

const DTScoreboard = styled.div`
  display: flex;
`;

const DTScoreboardPanel = styled.div`
  flex: 1;
  margin: ${({ theme }) => theme.space[2]};
  padding: ${({ theme }) => theme.space[2]};
  border: ${({ theme }) => theme.borders[3]};
  border-radius: ${({ theme }) => theme.radii[2]};
  text-align: center;
`;

const DTScoreboardHeading = styled.h3`
  margin: ${({ theme }) => theme.space[1]};
  font-size: ${({ theme }) => theme.fontSizes[4]};
`;

const DTScoreboardValue = styled.div`
  position: relative;
  color: ${({ theme }) => theme.colors.white};
  font-size: 4rem;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    padding-top: 0.6rem;
    padding-bottom: 1rem;
  }
`;

const TopPlayer = styled.div`
  display: inline-block;
`;

const TopPlayerData = styled.div`
  text-align: start;
`;

const WhiteBoldLinkButton = styled(BoldLinkButton)`
  color: ${({ theme }) => theme.colors.white};
`;

const PitchWrapper = styled(Box)`
  border: ${({ theme }) => theme.borders[0]};
  border-radius: ${({ theme }) => theme.radii[2]};
`;

type Props = WithTranslation;

const DreamTeam: React.FC<Props> = ({ t }) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [elementForMenu, setElementForMenu] = useState<number>(0);

  const data = useSelector((state: RootState) =>
    eventId
      ? getEventDreamTeam(state, Number(eventId))
      : getOverallDreamTeam(state),
  );

  const elementsById = useSelector(getElementsById);
  const elementsDataById = useSelector((state: RootState) =>
    getElementsEventDataById(state, Number(eventId)),
  );
  const event = useSelector(getEventsById)[Number(eventId)];
  const fixturesById = useSelector((state: RootState) =>
    getFixturesForEventById(state, Number(eventId)),
  );
  const formation = useSelector((state: RootState) =>
    eventId
      ? getEventDreamTeamFormation(state, Number(eventId))
      : getOverallDreamTeamFormation(state),
  );
  const now = useSelector(getCurrentEvent);
  const teamsById = useSelector(getTeamsById);

  const showElementDialog = (elementId: number) =>
    dispatch(showElementSummary(elementId));

  const handleShowMenuForElement = (element: number) => {
    setElementForMenu(element);
  };

  const handleHideMenuForElement = () => {
    setElementForMenu(0);
  };

  const handlePreviousClick = () => {
    if (event) {
      navigate(`/dream-team/${event.id - 1}`);
    }
  };

  const handleNextClick = () => {
    if (event) {
      navigate(`/dream-team/${event.id + 1}`);
    }
  };

  useEffect(() => {
    if (event) {
      dispatch(fetchEventDreamTeam(event.id));
      dispatch(fetchEventLive(event.id));
    } else {
      dispatch(fetchOverallDreamTeam());
    }
  }, [dispatch, event]);

  if (!now) {
    return <NotFound />;
  }

  if (!data) {
    return null;
  }
  const picks = dreamTeamAsPickLight(data.team);
  const topPlayer = elementsById[data.top_player.id];

  // Button state logic - disable if no previous/next event exists
  const previousEnabled = Boolean(event && event.id > 1);
  const nextEnabled = Boolean(event && event.id < now.id);

  const previousClickHandler = previousEnabled
    ? handlePreviousClick
    : undefined;
  const nextClickHandler = nextEnabled ? handleNextClick : undefined;

  // For the overall team we just show the standard element dialog as there
  // is no explain
  if (elementForMenu && !event) {
    showElementDialog(elementForMenu);
  }

  // Create a new function on each render as data could have changed and
  // need to ensure a render of connected subcomponents
  const renderPickValue = event
    ? valueForPlayerElement({
        elementsById,
        elementsDataById,
        fixturesById,
        teamsById,
      })
    : (pick: IPickLight) => {
        const elementData = data.team.filter((e) => e.element === pick.element);
        return elementData.length ? elementData[0].points : null;
      };
  return (
    <Wrapper>
      <Main>
        <Box mx={2}>
          <Title>{t("dreamTeam.dt", "Dream Team")}</Title>
        </Box>
        {/* ROUND PAGER */}
        <DTScoreboardWrapper>
          {event ? (
            <Box my={2}>
              <PagerHeading>{event.name}</PagerHeading>
              <EventPager>
                <PagerButton>
                  <Button
                    width={1}
                    to={
                      previousEnabled
                        ? `/dream-team/${event.id - 1}`
                        : undefined
                    }
                    onClick={previousClickHandler}
                    variant="secondary"
                    disabled={!previousEnabled}
                    startIcon={<ControlArrowLeft />}
                  >
                    {t("dreamTeam.prev", "Previous")}
                  </Button>
                </PagerButton>
                <PagerButtonNext>
                  <Button
                    width={1}
                    to={nextEnabled ? `/dream-team/${event.id + 1}` : undefined}
                    onClick={nextClickHandler}
                    variant="secondary"
                    disabled={!nextEnabled}
                    endIcon={<ControlArrowRight />}
                  >
                    {t("dreamTeam.next", "Next")}
                  </Button>
                </PagerButtonNext>
              </EventPager>
            </Box>
          ) : (
            <Box mx={2}>
              <SubHeading>{t("dreamTeam.overall", "Overall")}</SubHeading>
            </Box>
          )}
          {/* SCOREBOARD */}

          <Box>
            <DTScoreboard>
              <DTScoreboardPanel>
                <DTScoreboardHeading>
                  {t("dreamTeam.topPlayer", "Top Player")}
                </DTScoreboardHeading>
                <Box mb={1} color="white">
                  <WhiteBoldLinkButton
                    onClick={() => showElementDialog(topPlayer.id)}
                  >
                    {topPlayer.web_name}
                    <ControlArrowRight />
                  </WhiteBoldLinkButton>
                </Box>
                <Box mb={1}>
                  <TopPlayer>
                    <Media
                      img={
                        <ElementShirt
                          elementId={topPlayer.id}
                          sizes="(min-width: 1024px) 55px, (min-width: 610px) 44px, 33px"
                          hasShadow={true}
                        />
                      }
                      centred={true}
                    >
                      <TopPlayerData>
                        <div>{teamsById[topPlayer.team].short_name}</div>
                        <div>
                          {data.top_player.points}{" "}
                          {t("dreamTeam.points", "Points")}
                        </div>
                      </TopPlayerData>
                    </Media>
                  </TopPlayer>
                </Box>
              </DTScoreboardPanel>
              <DTScoreboardPanel>
                <DTScoreboardHeading>
                  {t("dreamTeam.totalPoints", "Total Points")}
                </DTScoreboardHeading>
                <DTScoreboardValue>
                  {data.team.reduce((total, e) => total + e.points, 0)}
                </DTScoreboardValue>
                {event ? (
                  <BoldLink variant="light" to="/dream-team/">
                    {t("dreamTeam.overall", "Overall")}
                  </BoldLink>
                ) : (
                  <BoldLink variant="light" to={`/dream-team/${now.id}`}>
                    {t("dreamTeam.round", "Round")}
                  </BoldLink>
                )}
              </DTScoreboardPanel>
            </DTScoreboard>
          </Box>
        </DTScoreboardWrapper>
        {/* TEAM */}
        <PitchWrapper pt={4}>
          <Box mx={2} mb={2}>
            <Tabs centered>
              <TabPanel
                label={t("dreamTeam.pitchView", "Pitch View")}
                link="pitch"
              >
                <Box mx={-2} pt={4}>
                  <DreamTeamFormation
                    chipName={""}
                    eventId={Number(eventId)}
                    formation={formation}
                    picks={picks}
                    renderDreamTeam={() => null}
                    renderElementMenu={handleShowMenuForElement}
                    renderPickValue={renderPickValue}
                  />
                </Box>
              </TabPanel>
              <TabPanel
                label={t("dreamTeam.listView", "List View")}
                link="list"
              >
                <Box my={4}>
                  <DreamTeamTable
                    elements={data.team.map((e) => e.element)}
                    renderElementMenu={handleShowMenuForElement}
                    dataById={elementsDataById}
                  />
                </Box>
              </TabPanel>
            </Tabs>
          </Box>
        </PitchWrapper>
        {event && elementForMenu ? (
          <ElementExplainDialog
            elementId={elementForMenu}
            eventId={event.id}
            closeDialog={handleHideMenuForElement}
          />
        ) : null}
        {event && <Fixtures eventId={event.id} />}
      </Main>
      <Secondary>
        <span>&nbsp;</span>
      </Secondary>
    </Wrapper>
  );
};

export { DreamTeam as DreamTeamTest };

export default withTranslation()(DreamTeam);
